import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Suspense as _Suspense, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "spin"
}
const _hoisted_2 = { class: "spin" }

import { ThemeProvider } from "vue3-styled-components";
import { theme } from "./config/theme/themeVariables.js";
import { provide, onMounted, watch, ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuth0 } from "@auth0/auth0-vue";
import { setContext } from "apollo-link-context";
import { useStore } from "@/store";
import { rbac } from "@/lib/acl";
import { useI18n } from "vue-i18n";
import { PagerInput } from "@/generated/graphql";


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

let startUp = ref(false);
let router = useRouter();

const route = useRoute();
const store = useStore();
const { t } = useI18n({ inheritLocale: true, useScope: "global" });
const {
  isLoading,
  getAccessTokenSilently,
  loginWithRedirect,
  isAuthenticated,
  logout,
} = useAuth0();

const subscriptionStatus = computed(() => store.getters["subscription/status"]);

// provides
provide("translator", t);
provide("canAccess", (action: string) =>
  rbac.can(store.getters.currentUserRoles, action)
);
provide("pager", {
  orderBy: null,
  orderDirection: null,
  limit: 5,
  offset: 0,
} as PagerInput);
provide("capitalize", (v: string) =>
  v ? v.charAt(0).toUpperCase() + v.slice(1) : ""
);
provide("formatDateAdded", (dbDateTime) => {
  const date = new Date(dbDateTime);
  return {
    name: `${date.toLocaleString("default", {
      month: "short",
    })} ${date.getDate()}, ${date.getFullYear()}`,
    helped: `${date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })}`,
    date,
  };
});

// wait until Auth0 finishes bootstrapping
const waitAuth0Ready = () =>
  new Promise<void>((resolve) => {
    if (!isLoading.value) return resolve();
    const stop = watch(isLoading, (v) => {
      if (!v) {
        stop();
        resolve();
      }
    });
  });

async function handleSubscription() {
  await store.dispatch("subscription/getInstitutionSubscription");
  const activeSub = store.state.currentUserIsActiveSub;

  if (
    subscriptionStatus.value === "TRIAL_EXPIRED" ||
    subscriptionStatus.value === "GRACE_EXPIRED" ||
    !activeSub
  ) {
    if (route.name !== "BillingPlansSeparate") {
      router.push({ name: "expired" });
    }
  }
  return;
}

const initialPath =
  window.location.pathname +
  window.location.search +
  window.location.hash;

onMounted(async () => {
  async function waitRouteReady() {
    await router.isReady();
    while (true) {
      if (route.matched.length > 0) break;
      await new Promise((r) => setTimeout(r, 5));
    }
  }

  await waitRouteReady();

  const isPublic = route.matched.some((r) => r.meta?.roles?.includes("public"));

  // public routes — no auth checks at all
  if (isPublic) {
    startUp.value = true;
    return;
  }

  // дочекаємось поки Auth0 скаже своє слово
  await waitAuth0Ready();


  if (!isAuthenticated.value) {
    localStorage.removeItem("apollo-token");
    router.push({ name: "trialSignUp" });
    startUp.value = true;
    return;
  }

  try {
    // single source of truth: if silent token works — user is "logged in"
    const token = await getAccessTokenSilently();
    if (!token) throw new Error("NO_TOKEN");

    setContext((_, { headers }) => ({
      headers: { ...headers, Authorization: `Bearer ${token}` },
    }));

    localStorage.setItem("apollo-token", token);

    await store.dispatch("GET_CURRENT_USER");
    await handleSubscription()

    startUp.value = true;

  } catch (err: any) {

    
    const code = String(err?.error || err?.message || err || "");
    const hadToken = !!localStorage.getItem("apollo-token");

    if (code.includes("EXPIRED")) {
      // NOTE: GET_CURRENT_USER throws EXPIRED for business reasons (subscription state),
      // not an auth error. We intentionally handle it here to avoid trial fallback.
      await handleSubscription();
      return;
    }
    console.log("AUTH_ERROR", code, err);

    // not authenticated in Auth0 / cannot get silent token
    if (code.includes("login_required") || code.includes("NO_TOKEN")) {
      // always clear local token when silent auth fails
      localStorage.removeItem("apollo-token");

      if (hadToken) {
        // user had a token before → treat as logged-in session,
        // send them to Auth0 login screen to refresh session
        await loginWithRedirect({
          appState: { targetUrl: window.location.pathname },
        });
      } else {
        // first-time visitor / no local token → show trial signup page
        router.push({ name: "trialSignUp" });
        startUp.value = true;
      }

      return;
    }

    // Auth0 user exists but our backend user was not found
    if (code.includes("USER_NOT_FOUND")) {
      await logout({ returnTo: window.location.origin }); // fully reset Auth0 session
      router.push({ name: "trialSignUp" });
      startUp.value = true;
      return;
    }

    // fallback for any other errors
    router.push({ name: "trialSignUp" });
    startUp.value = true;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_unref(isLoading) && _unref(startUp))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_component_a_spin)
      ]))
    : (_openBlock(), _createBlock(_unref(ThemeProvider), {
        key: 1,
        style: {"height":"100%"},
        theme: { ..._unref(theme), rtl: false, darkMode: false, topMenu: false }
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_Suspense, null, {
            default: _withCtx(() => [
              _createVNode(_component_router_view)
            ]),
            fallback: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_a_spin)
              ])
            ]),
            _: 1
          }))
        ]),
        _: 1
      }, 8, ["theme"]))
}
}

})